import { ZoomIn, ZoomOut, Maximize2, ScanSearch, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/i18n/LanguageContext';
import { formatPercent, languageToLocale } from '@/lib/locale';
import { useZoomVariant, ZOOM_VARIANT_LABELS, type ZoomVariant } from '@/lib/zoomVariant';
import { usePlan } from '@/hooks/usePlan';

export interface CanvasScaleControlsProps {
  contentScale: number;
  minScale: number;
  maxScale: number;
  onScaleUp: () => void;
  onScaleDown: () => void;
  onResetScale: () => void;
  /** Fas 2f: fit-to-screen-knapp. Klampas inom tier-range; om content
   *  inte ryms vid min hamnar centroid i mitten av viewport. */
  onFitToScreen?: () => void;
  /** Fas 2f: zoomMode för att visa rätt tooltip ('static' vs 'dynamic'). */
  zoomMode?: 'static' | 'dynamic';
}

/**
 * Sticky zoom controls used inside WorkflowCanvas.
 * Extracted to keep WorkflowCanvas small enough to push without timeouts.
 *
 * Fas 2f: visar tier-range i tooltip + ny "fit-to-screen"-knapp som
 * passar allt content i viewporten (klampad inom tier-range).
 */
export function CanvasScaleControls({
  contentScale,
  minScale,
  maxScale,
  onScaleUp,
  onScaleDown,
  onResetScale,
  onFitToScreen,
  zoomMode,
}: CanvasScaleControlsProps) {
  const { language } = useTranslation();
  const [variant, setVariant] = useZoomVariant();
  const { tier } = usePlan();
  // Variant-toggle är bara tillgängligt för Max + Enterprise. Free/Advanced/
  // Pro använder tier-derivd static-zoom som default; deras UX är specat.
  // Max/Enterprise har dynamic-zoom där det finns flera rendering-strategier
  // med olika tradeoffs (block-jumps, transitions) — så de får välja själva.
  const showVariantToggle = tier === 'max' || tier === 'enterprise';
  const displayPercent = formatPercent(contentScale, languageToLocale(language));
  const minPct = Math.round(minScale * 100);
  const maxPct = Math.round(maxScale * 100);
  const modeLabel = zoomMode === 'dynamic' ? 'dynamisk' : 'statisk';
  const rangeTooltip = `Zoom ${minPct}–${maxPct}% (${modeLabel})`;
  // Max/Enterprise: 'tier-default' resolverar redan till dynamic-no-transition
  // (snap) i WorkflowCanvas. Vi lyfter den explicita "Snap"-varianten överst
  // + märker den som rekommenderad så user ser att det är standard, men
  // behåller alla alternativ i reglaget om någon vill testa annat.
  const variantOptions: { value: ZoomVariant; description: string }[] = [
    { value: 'dynamic-no-transition', description: 'Snap — instant, blocks teleporterar pga växande coord-rymd (rekommenderad för Max/Enterprise)' },
    { value: 'tier-default', description: 'Använd tier-derivd zoom (= Snap för Max/Enterprise)' },
    { value: 'force-static', description: 'Uniform shrink — block stannar still, canvas krymper' },
    { value: 'dynamic-with-transition', description: 'Pre-split — transition på, blocks kan hoppa' },
  ];

  return (
    <div className="flex items-center gap-2 bg-card/90 backdrop-blur-sm rounded-lg border border-border p-1 shadow-lg pointer-events-auto">
      <Button
        variant="ghost"
        size="icon"
        onClick={onScaleDown}
        disabled={contentScale <= minScale}
        className="h-8 w-8"
        title={`Förminska — ${rangeTooltip}`}
      >
        <ZoomOut className="h-4 w-4" />
      </Button>
      <button
        onClick={onResetScale}
        className="min-w-[60px] text-sm font-medium text-foreground hover:bg-accent rounded px-2 py-1 transition-colors"
        title={`Återställ skala (100%) — ${rangeTooltip}`}
      >
        {displayPercent}
      </button>
      <Button
        variant="ghost"
        size="icon"
        onClick={onScaleUp}
        disabled={contentScale >= maxScale}
        className="h-8 w-8"
        title={`Förstora — ${rangeTooltip}`}
      >
        <ZoomIn className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-border" />
      {onFitToScreen && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onFitToScreen}
          className="h-8 w-8"
          title="Passa allt content (fit to screen)"
          data-testid="zoom-fit-to-screen"
        >
          <ScanSearch className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={onResetScale}
        className="h-8 w-8"
        title="Återställ till 100%"
      >
        <Maximize2 className="h-4 w-4" />
      </Button>
      {showVariantToggle && (
        <>
          <div className="w-px h-6 bg-border" />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Zoom-variant — välj rendering-strategi"
                data-testid="zoom-variant-toggle"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end">
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold">Zoom-variant</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Växla rendering-strategi. Persisteras i localStorage.
                  </p>
                </div>
                <RadioGroup
                  value={variant}
                  onValueChange={(v) => setVariant(v as ZoomVariant)}
                  className="space-y-2"
                >
                  {variantOptions.map((opt) => (
                    <div key={opt.value} className="flex items-start gap-2">
                      <RadioGroupItem value={opt.value} id={`zv-${opt.value}`} className="mt-0.5" />
                      <Label htmlFor={`zv-${opt.value}`} className="cursor-pointer flex-1">
                        <div className="text-sm font-medium">{ZOOM_VARIANT_LABELS[opt.value]}</div>
                        <div className="text-xs text-muted-foreground font-normal">{opt.description}</div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
}
